import style from './PortalSidebar.module.scss';

import classNames from 'classnames';
import Link from 'next/link';
import PropTypes from 'prop-types';
import {
  Children,
  Fragment,
  useCallback,
  useEffect,
  useState
} from 'react';

import { trackButtonClicks } from '@/utility/analytics';
import { t } from '@/utility/localization';

import { useCommunityPortalContext } from '@/contexts/CommunityPortalContext';
import { useWindowWidthContext } from '@/contexts/WindowWidthContext';

import ProgressRing from '@/components/common/ProgressRing';
import ToolTipWithImage from '@/components/common/ToolTipWithImage';
import NPLIconButton from '@/components/npl/NPLIconButton';

import { trackLeftNavItemClick } from '../../utils';

// for now we will be using the existing image component,
// and if there is time we will create/migrate to a new image component

// TODO: There's two issues
// 2. Vertical Scroll - The PortalSidebar tray on the lg and xl screens should have full height

const className = 'c-PortalSidebar';
const PortalSidebar = ({ pageItems, user, customClassNames }) => {
  const { isGtEqLg } = useWindowWidthContext();
  const { activeCommunity } = useCommunityPortalContext();
  const [mountTooltip, setMountTooltip] = useState(false);

  const handleLeftNavItemClick = useCallback(
    (navItem, index) => {
      const { label, link } = navItem;
      trackLeftNavItemClick(navItem, user, activeCommunity);

      trackButtonClicks({
        button: {
          text: label,
          location: 'Portal Sidebar',
          description: `Side bar navigation in the portal. Position at index ${index}`,
          event: `Navigate to ${label}`,
          eventDescription: `Redirects to ${link}`
        },
        community: {
          name: activeCommunity?.title,
          code: activeCommunity?.code,
          id: activeCommunity?._id
        }
      });
    },
    [activeCommunity, user]
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      setMountTooltip(true);
    }, 1000);
    return () => clearTimeout(timeout);
  }, []);

  const renderNavItems = useCallback(
    (subNavItems) =>
      Children.toArray(
        subNavItems.map((navItem, index) => {
          const {
            label,
            link,
            isActive,
            Icon,
            show,
            isActionable,
            rightIcon,
            showProgressRing,
            onDismissalCtaClick,
            progress,
            testId,
            isNew
          } = navItem;

          if (!show) return;
          const colorClassName = isActive
            ? 'fill-npl-yellow-light-alpha-11 text-npl-yellow-light-alpha-11'
            : 'fill-npl-text-secondary-on-light text-npl-text-secondary-on-light';

          const renderLink = () => {
            return (
              <div className="flex w-full items-center justify-between h-auto">
                <div
                  role="button"
                  tabIndex={0}
                  data-test-id={testId}
                  onClick={() => handleLeftNavItemClick(navItem, index)}
                  className={classNames(
                    'w-full rounded-[24px] px-12 py-8 transition-all group',
                    isActive
                      ? 'bg-npl-yellow-light-alpha-3 !hover:bg-npl-yellow-light-alpha-3'
                      : 'hover:bg-npl-neutral-light-alpha-2'
                  )}>
                  <div className="flex items-center justify-between lg:justify-start">
                    <div className="flex flex-1 items-center space-x-12 w-full">
                      {isGtEqLg && Icon && (
                        <div className="flex h-24 w-24 min-w-[24px] items-center justify-center">
                          <Icon
                            className={colorClassName}
                            width="20"
                            height="20"
                          />
                        </div>
                      )}

                      {showProgressRing ? (
                        <div className="flex h-24 w-24 min-w-[24px] items-center justify-center">
                          <ProgressRing progress={progress} />
                        </div>
                      ) : null}
                      <span
                        className={`text-label-lg w-full font-medium ${colorClassName}`}>
                        {label}
                      </span>
                      {onDismissalCtaClick && (
                        <div className="group-hover:opacity-100 opacity-0 ml-auto flex items-center">
                          <NPLIconButton
                            size="xs"
                            hierarchy="plain"
                            icon="x-close"
                            onClick={onDismissalCtaClick}
                          />
                        </div>
                      )}
                    </div>
                    {isActionable && (
                      <div className="h-8 w-8 rounded-full bg-npl-yellow-light-solid-9" />
                    )}

                    {rightIcon && rightIcon()}

                    {isNew && (
                      <p className="flex items-center space-x-2 rounded-4 bg-npl-yellow-light-solid-3 px-2 text-overline-xs font-semibold text-npl-yellow-light-solid-11">
                        <Icon
                          name="stars-filled-02"
                          width="12"
                          height="12"
                          className="fill-npl-yellow-light-solid-11"
                        />
                        <span>{t('new-caps')}</span>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          };

          const renderTooltip = () => {
            return (
              <div className="relative flex flex-col items-stretch">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navItem?.onDismissTooltip();
                  }}
                  className="absolute right-4 top-4 flex aspect-square w-32 items-center justify-center rounded-full hover:bg-npl-neutral-light-solid-3">
                  <Icon name="x-close" width={16} height={16} />
                </div>
                <div className="flex flex-col p-16">
                  <div className="flex flex-row items-center">
                    <span className="flex-grow text-body-md font-medium text-npl-text-icon-on-light-surface-primary">
                      {navItem.tooltipProps?.title}
                    </span>
                  </div>
                  <span className="text-body-sm text-npl-text-icon-on-light-surface-tertiary">
                    {navItem.tooltipProps?.description}
                  </span>
                  {navItem.tooltipProps?.link && (
                    <span className="mt-8 text-body-sm text-npl-text-icon-on-light-surface-tertiary underline">
                      <a
                        href={navItem.tooltipProps?.link}
                        target="_blank"
                        rel="noreferrer">
                        {t('read-more1')}
                      </a>
                    </span>
                  )}
                </div>
                <img
                  src={navItem.tooltipProps?.imageSrc}
                  alt={navItem.tooltipProps?.title}
                  className="max-w-300 rounded-b-8"
                />
              </div>
            );
          };
          return (
            <Link
              href={link}
              key={index}
              className="flex w-full items-center justify-between">
              {mountTooltip && navItem.tooltipProps ? (
                <ToolTipWithImage
                  direction="right-start"
                  open={navItem.tooltipProps?.show}
                  renderComponent={renderTooltip()}>
                  {renderLink()}
                </ToolTipWithImage>
              ) : (
                renderLink()
              )}
            </Link>
          );
        })
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageItems]
  );

  return (
    <div
      className={classNames(
        className,
        style[className],
        'w-full bg-white-default px-8 py-12 h-auto',
        customClassNames
      )}>
      <div className="flex flex-col justify-between w-full">
        <div className="flex flex-row lg:w-full lg:flex-col">
          <nav
            className={classNames(
              `${className}__left_nav_panel ${
                style[`${className}__left_nav_panel`]
              } liner flex items-center overflow-x-auto mb-60 whitespace-nowrap lg:flex-col lg:items-stretch lg:overflow-hidden `
            )}>
            {pageItems?.map((subNav, index) => (
              <Fragment key={index}>
                {index !== 0 && (
                  <span className="m-12 border-b-[1px] border-npl-separator-alpha" />
                )}

                {subNav.title && (
                  <span className="mb-8 ml-12 text-overline-xs font-semibold uppercase text-npl-neutral-light-solid-8">
                    {subNav.title}
                  </span>
                )}

                {renderNavItems(subNav.items)}
              </Fragment>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
};

const PageItemPropTypes = {
  label: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
  Icon: PropTypes.elementType,
  isActive: PropTypes.bool
};

const SubNavItems = {
  title: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.shape(PageItemPropTypes))
};

PortalSidebar.propTypes = {
  pageItems: PropTypes.arrayOf(PropTypes.shape(SubNavItems)),
  customClassNames: PropTypes.string
};

export default PortalSidebar;
